'use client'

import {
  useCreateTagMutation,
  useDeleteTagMutation,
  useGetWorkspaceTagsQuery,
} from '@/lib/features/workflow/workflowApi'
import { Plus, Search, Trash2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { TagPill } from './TagPill'

interface TagPickerProps {
  workspaceId: string
  excludeTagIds?: string[]
  onSelectTag: (tagId: string) => void
}

export function TagPicker({ workspaceId, excludeTagIds = [], onSelectTag }: TagPickerProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateButton, setShowCreateButton] = useState(false)

  const { data: tags = [], isLoading } = useGetWorkspaceTagsQuery(workspaceId)
  const [createTag, { isLoading: isCreating }] = useCreateTagMutation()
  const [deleteTag] = useDeleteTagMutation()

  const availableTags = useMemo(() => {
    return tags.filter((tag) => !excludeTagIds.includes(tag.id))
  }, [tags, excludeTagIds])

  const { globalTags, workspaceTags } = useMemo(() => {
    const global = availableTags.filter((tag) => tag.workspaceId === null)
    const workspace = availableTags.filter((tag) => tag.workspaceId !== null)
    return { globalTags: global, workspaceTags: workspace }
  }, [availableTags])

  const filteredTags = useMemo(() => {
    if (!searchQuery) return { globalTags, workspaceTags }

    const query = searchQuery.toLowerCase()
    return {
      globalTags: globalTags.filter((tag) => tag.name.toLowerCase().includes(query)),
      workspaceTags: workspaceTags.filter((tag) => tag.name.toLowerCase().includes(query)),
    }
  }, [globalTags, workspaceTags, searchQuery])

  const exactMatch = useMemo(() => {
    return availableTags.find((tag) => tag.name.toLowerCase() === searchQuery.toLowerCase())
  }, [availableTags, searchQuery])

  const handleCreateTag = async () => {
    if (!searchQuery.trim()) return

    try {
      const result = await createTag({ name: searchQuery.trim(), workspaceId }).unwrap()
      onSelectTag(result.tag.id)
      setSearchQuery('')
      setShowCreateButton(false)
      toast.success(`Created tag "${result.tag.name}"`)
    } catch (error) {
      console.error('Failed to create tag:', error)
      toast.error('Failed to create tag')
    }
  }

  const handleDeleteTag = async (tagId: string, tagName: string, e: React.MouseEvent) => {
    e.stopPropagation()

    if (!confirm(`Delete tag "${tagName}"? This will remove it from all workflows.`)) {
      return
    }

    try {
      await deleteTag(tagId).unwrap()
      toast.success(`Deleted tag "${tagName}"`)
    } catch (error) {
      console.error('Failed to delete tag:', error)
      toast.error('Failed to delete tag')
    }
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
        <input
          type="text"
          placeholder="Search or create tag..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            setShowCreateButton(e.target.value.trim().length > 0)
          }}
          className="w-full rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] py-2 pl-10 pr-4 text-sm focus:border-[#B0B0B0] focus:outline-none"
        />
      </div>

      {isLoading ? (
        <div className="py-4 text-center text-sm text-gray-500">Loading tags...</div>
      ) : (
        <>
          {filteredTags.globalTags.length + filteredTags.workspaceTags.length > 0 ? (
            <div className="flex max-h-48 flex-col gap-2 overflow-y-auto">
              {filteredTags.globalTags.length > 0 && (
                <div>
                  <div className="mb-1 px-3 text-xs font-semibold text-gray-500">Global Tags</div>
                  <div className="flex flex-col gap-1">
                    {filteredTags.globalTags.map((tag) => (
                      <button
                        key={tag.id}
                        onClick={() => {
                          onSelectTag(tag.id)
                          setSearchQuery('')
                          setShowCreateButton(false)
                        }}
                        className="flex items-center gap-2 rounded-lg px-3 py-2 text-left transition-colors hover:bg-gray-100"
                      >
                        <TagPill name={tag.name} isGlobal={true} readonly />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {filteredTags.workspaceTags.length > 0 && (
                <div>
                  <div className="mb-1 px-3 text-xs font-semibold text-gray-500">
                    Workspace Tags
                  </div>
                  <div className="flex flex-col gap-1">
                    {filteredTags.workspaceTags.map((tag) => (
                      <div
                        key={tag.id}
                        className="group flex items-center justify-between gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-gray-100"
                      >
                        <button
                          onClick={() => {
                            onSelectTag(tag.id)
                            setSearchQuery('')
                            setShowCreateButton(false)
                          }}
                          className="flex flex-1 items-center text-left"
                        >
                          <TagPill name={tag.name} isGlobal={false} readonly />
                        </button>
                        <button
                          onClick={(e) => handleDeleteTag(tag.id, tag.name, e)}
                          className="opacity-0 transition-opacity group-hover:opacity-100"
                          title="Delete tag"
                        >
                          <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : searchQuery ? (
            <div className="py-4 text-center text-sm text-gray-500">No tags found</div>
          ) : (
            <div className="py-4 text-center text-sm text-gray-500">No tags available</div>
          )}

          {showCreateButton && !exactMatch && (
            <button
              onClick={handleCreateTag}
              disabled={isCreating}
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <Plus size={16} />
              Create workspace tag &quot;{searchQuery}&quot;
            </button>
          )}
        </>
      )}
    </div>
  )
}
